/**
 * Step-up state — handoff-v2 item 13, client half.
 *
 * The pattern every destructive button uses:
 *
 *     await stepUp.guard(() => api.patch(`/admin/portals/${id}/enabled`, { enabled }))
 *
 * `guard` runs the action. If the server answers `step_up_required`, the action
 * is held, a modal asks *confirm it is you*, and on confirmation the **same**
 * closure runs again. That last part is the whole design: the retry must be the
 * original action, not a re-derived one, or the thing confirmed and the thing
 * performed can drift apart between the two attempts.
 *
 * Failure is a modal, never a silent 403 — the person hitting this is the owner,
 * and telling them they are not allowed would be both wrong and alarming.
 */
import { ApiError } from "./api";
import { reauthenticate } from "./auth";

class StepUp {
  /** Non-null while the modal is open. */
  pending = $state<{ label: string } | null>(null);

  /**
   * Non-null while the type-to-confirm modal is open.
   *
   * Separate state from `pending` rather than a mode flag on it, because the two
   * can chain: a stale token on a protected project raises step-up first, and the
   * confirmation challenge only arrives on the retry.
   */
  confirming = $state<{ expected: string; project: string; typed: string } | null>(null);
  busy = $state(false);
  error = $state<string | null>(null);

  #retry: ((confirm?: string) => Promise<unknown>) | null = null;
  #resolve: ((ok: boolean) => void) | null = null;

  /**
   * Run an action, transparently handling a step-up challenge.
   *
   * Resolves true when the action completed (first try or after confirming),
   * false when the person cancelled. Any other error propagates — a 400 from a
   * bad value is the caller's to report, not this class's to swallow.
   */
  async guard(action: (confirm?: string) => Promise<unknown>, label = "this change"): Promise<boolean> {
    try {
      await action();
      return true;
    } catch (e) {
      if (!(e instanceof ApiError)) throw e;

      // Two different challenges, one retry mechanism. Both re-run the ORIGINAL
      // closure so the thing confirmed and the thing performed cannot drift.
      if (e.needsConfirmation) {
        const expected = e.expectedPhrase;
        if (!expected) throw e;
        this.#retry = action;
        this.error = null;
        this.confirming = { expected, project: String(e.detail?.project ?? ""), typed: "" };
        return new Promise<boolean>((resolve) => {
          this.#resolve = resolve;
        });
      }

      if (!e.needsStepUp) throw e;

      this.#retry = action;
      this.error = null;
      this.pending = { label };
      return new Promise<boolean>((resolve) => {
        this.#resolve = resolve;
      });
    }
  }

  /**
   * Submit the typed phrase. The server checks it again regardless — this method
   * only saves a round-trip on an obvious mismatch.
   */
  async confirmTyped(): Promise<void> {
    const c = this.confirming;
    if (!c || !this.#retry) return;
    if (c.typed.trim() !== c.expected) {
      this.error = `That does not match. Type ${c.expected} exactly.`;
      return;
    }
    this.busy = true;
    this.error = null;
    try {
      await this.#retry(c.typed.trim());
      this.#finish(true);
    } catch (e) {
      // A protected project can ALSO need step-up. Hand over rather than fail.
      if (e instanceof ApiError && e.needsStepUp) {
        this.confirming = null;
        this.pending = { label: `${c.expected} in ${c.project}` };
        this.busy = false;
        return;
      }
      this.error = e instanceof Error ? e.message : "That did not work.";
      this.busy = false;
    }
  }

  async confirm(): Promise<void> {
    if (!this.#retry) return;
    this.busy = true;
    this.error = null;
    try {
      await reauthenticate();
      await this.#retry();
      this.#finish(true);
    } catch (e) {
      // A closed popup is a decision, not a fault. Anything else is worth saying.
      const code = (e as { code?: string }).code ?? "";
      if (/popup-closed-by-user|cancelled-popup-request/.test(code)) {
        this.#finish(false);
        return;
      }
      // Fresh token, but the target is a protected project: the second challenge
      // only becomes visible once the first is satisfied. Hand over to it rather
      // than reporting "could not confirm" on a step-up that in fact worked.
      if (e instanceof ApiError && e.needsConfirmation && e.expectedPhrase) {
        this.pending = null;
        this.confirming = { expected: e.expectedPhrase, project: String(e.detail?.project ?? ""), typed: "" };
        this.error = null;
        return;
      }
      this.error = e instanceof ApiError ? e.message : "Could not confirm. Try again.";
    } finally {
      this.busy = false;
    }
  }

  cancel(): void {
    this.#finish(false);
  }

  #finish(ok: boolean): void {
    this.pending = null;
    this.confirming = null;
    this.busy = false;
    this.#retry = null;
    this.error = null;
    this.#resolve?.(ok);
    this.#resolve = null;
  }
}

export const stepUp = new StepUp();
